import Task, { GroupByColumn, TaskGroup, TaskGroupAttributes } from "../../../Types/project-task.type";

export type GroupByPositions = { [groupKey: string]: number };
// Load saved positions from local storage once
export const loadGroupPositions = (projectId: number): GroupByPositions => {
    const projectKey = `task-group-positions-${projectId}`;
    const savedPositions = localStorage.getItem(projectKey);
    return savedPositions ? JSON.parse(savedPositions) : {};
};
const getGroupPositions = (taskGroups: TaskGroup[]) => {
    return taskGroups.reduce((acc, group, index) => {
        const groupKey = group.attributes.section || `group-${index}`;
        acc[groupKey] = index;
        return acc;
    }, {} as GroupByPositions);
};
// Function to save positions to local storage
export const saveGroupPositions = (projectId: number, taskGroups: TaskGroup[]) => {
    const positions = getGroupPositions(taskGroups)
    const projectKey = `task-group-positions-${projectId}`;
    localStorage.setItem(projectKey, JSON.stringify(positions));
};

export const isSectionGrouping = (groupByColumns: GroupByColumn[]) => {
    return groupByColumns.length === 1 && groupByColumns[0] === "section"
}

export const getTaskGroupIndeses = (
    taskGroups: TaskGroup[],
    task_id: number
) => {
    let groupIndex: number | null = null;
    let taskIndex: number | null = null;

    // Find the current group and position of the updated task
    for (let gIndex = 0; gIndex < taskGroups.length; gIndex++) {
        const group = taskGroups[gIndex];
        for (let tIndex = 0; tIndex < group.tasks.length; tIndex++) {
            const task = group.tasks[tIndex];
            if (task.task_id === task_id) {
                groupIndex = gIndex;
                taskIndex = tIndex;
                return { groupIndex, taskIndex }
            }
        }
    }
    return null
}

/**
 * Repositions a task within the task groups based on the specified grouping criteria.
 * 
 * This function:
 * 1. Identifies the current group and position of the updated task in the existing task groups.
 * 2. If the task remains in the same group, it updates the task in place.
 * 3. If the task needs to move to a different group, it is removed from the old group.
 * 4. The function finds or creates a new group based on the specified `groupByColumns`.
 * 5. Returns an updated list of `TaskGroup` objects reflecting the new task position.
 * 
 * @param {TaskGroup[]} taskGroups - The current list of task groups.
 * @param {Task} updatedTask - The task that needs to be repositioned.
 * @param {GroupByColumn[]} [groupByColumns] - The columns used to determine task grouping.
 * 
 * @returns {TaskGroup[]} - A new array of task groups with the updated task repositioned correctly.
 * 
 * @example
 * const updatedTaskGroups = taskReposition(taskGroups, updatedTask, [GroupByColumn.AssigneeID, GroupByColumn.Status]);
 * 
 * // This will reposition `updatedTask` within `taskGroups`, grouping by AssigneeID and Status.
 */
export function taskReposition(
    taskGroups: TaskGroup[],
    updatedTask: Task,
    groupByColumns: GroupByColumn[]
): TaskGroup[] {
    let currentGroupIndex: number | null = null;
    let currentTaskIndex: number | null = null;

    // Find the current group and position of the updated task
    taskGroups.forEach((group, groupIndex) => {
        group.tasks.forEach((task, taskIndex) => {
            if (task.task_id === updatedTask.task_id) {
                currentGroupIndex = groupIndex;
                currentTaskIndex = taskIndex;
            }
        });
    });

    // Determine the new group where the task should be placed
    let newGroupIndex: number | null = null;

    taskGroups.forEach((group, groupIndex) => {
        const groupMatches = groupByColumns.every(
            (column) => group.attributes[column] === (updatedTask as any)[column]
        );

        if (groupMatches) {
            newGroupIndex = groupIndex;
        }
    });

    // Create a new immutable array
    const newTaskGroups = [...taskGroups];

    if (currentGroupIndex !== null && currentTaskIndex !== null) {
        // If the task remains in the same group, update it in place
        if (currentGroupIndex === newGroupIndex) {
            newTaskGroups[currentGroupIndex] = {
                ...newTaskGroups[currentGroupIndex],
                tasks: newTaskGroups[currentGroupIndex].tasks.map(task =>
                    task.task_id === updatedTask.task_id ? updatedTask : task
                ),
            };
            return newTaskGroups; // Return updated groups
        }

        // Remove task from its current group
        newTaskGroups[currentGroupIndex] = {
            ...newTaskGroups[currentGroupIndex],
            tasks: newTaskGroups[currentGroupIndex].tasks.filter(
                (task) => task.task_id !== updatedTask.task_id
            ),
        };
    }

    // If the task needs a new group, set up the correct attributes
    if (newGroupIndex !== null) {
        newTaskGroups[newGroupIndex] = {
            ...newTaskGroups[newGroupIndex],
            tasks: [...newTaskGroups[newGroupIndex].tasks, updatedTask],
        };
    } else {
        // Extract attributes dynamically from updatedTask based on `groupByColumns`
        const newGroupAttributes: TaskGroupAttributes = {};

        groupByColumns.forEach((column) => {
            if (updatedTask[column] !== undefined) {
                newGroupAttributes[column] = updatedTask[column] as string;
            }
        });

        // Create a new group with proper attributes
        newTaskGroups.push({
            attributes: newGroupAttributes,
            tasks: [updatedTask],
        });
    }

    return newTaskGroups;
}


/**
 * Repositions multiple updated tasks within the task groups based on the specified grouping criteria.
 * 
 * This function:
 * 1. Finds the current position of each updated task in the existing task groups.
 * 2. Removes the tasks from their old groups while maintaining immutability.
 * 3. Determines the appropriate group for each updated task based on the specified `groupByColumns`.
 * 4. If a matching group exists, the task is added to that group; otherwise, a new group is created.
 * 5. Returns an updated list of `TaskGroup` objects reflecting the new task positions.
 * 
 * @param {TaskGroup[]} taskGroups - The current list of task groups.
 * @param {Task[]} updatedTasks - The list of tasks to reposition.
 * @param {GroupByColumn[]} [groupByColumns] - The columns used to determine task grouping.
 * 
 * @returns {TaskGroup[]} - A new array of task groups with the updated tasks repositioned correctly.
 * 
 * @example
 * const updatedTaskGroups = taskReposition(taskGroups, updatedTasks, [GroupByColumn.AssigneeID, GroupByColumn.Status]);
 * 
 * // This will reposition `updatedTasks` within `taskGroups`, grouping by AssigneeID and Status.
 */

export function tasksReposition(
    taskGroups: TaskGroup[],
    updatedTasks: Task[],
    groupByColumns: GroupByColumn[]
): TaskGroup[] {
    // Step 1: Create a map to track current task positions for quick lookup
    const taskGroupMap = new Map<number, { groupIndex: number; taskIndex: number }>();

    taskGroups.forEach((group, groupIndex) => {
        group.tasks.forEach((task, taskIndex) => {
            taskGroupMap.set(task.task_id, { groupIndex, taskIndex });
        });
    });

    // Step 2: Create a new array to maintain immutability
    let newTaskGroups = [...taskGroups];

    // Step 3: Remove tasks from their old groups
    updatedTasks.forEach((updatedTask) => {
        const position = taskGroupMap.get(updatedTask.task_id);
        if (position) {
            const { groupIndex, taskIndex } = position;
            newTaskGroups[groupIndex] = {
                ...newTaskGroups[groupIndex],
                tasks: newTaskGroups[groupIndex].tasks.filter(
                    (task) => task.task_id !== updatedTask.task_id
                ),
            };
        }
    });

    // Step 4: Reorganize tasks into new groups
    updatedTasks.forEach((updatedTask) => {
        // Find the new group where the task should be placed
        let newGroupIndex: number | null = null;

        newTaskGroups.forEach((group, groupIndex) => {
            const groupMatches = groupByColumns.every(
                (column) => group.attributes[column] === (updatedTask as any)[column]
            );
            if (groupMatches) {
                newGroupIndex = groupIndex;
            }
        });

        // If the group exists, add the task to it
        if (newGroupIndex !== null) {
            newTaskGroups[newGroupIndex] = {
                ...newTaskGroups[newGroupIndex],
                tasks: [...newTaskGroups[newGroupIndex].tasks, updatedTask],
            };
        } else {
            // Step 5: Create a new group with appropriate attributes if no match exists
            const newGroupAttributes: TaskGroupAttributes = {};

            groupByColumns.forEach((column) => {
                if (updatedTask[column] !== undefined) {
                    newGroupAttributes[column] = updatedTask[column] as string;
                }
            });

            newTaskGroups.push({
                attributes: newGroupAttributes,
                tasks: [updatedTask],
            });
        }
    });

    return newTaskGroups;
}
/**
 * Adds and repositions a new task within the task groups based on the specified grouping criteria.
 *
 * This function:
 * 1. Determines the appropriate group for the new task based on `groupByColumns`.
 * 2. If a matching group exists, the task is added to that group.
 * 3. If no matching group is found, a new group is created with the appropriate attributes.
 * 4. Returns an updated list of `TaskGroup` objects reflecting the new task placement.
 *
 * @param {TaskGroup[]} taskGroups - The current list of task groups.
 * @param {Task} newTask - The new task to add and reposition.
 * @param {GroupByColumn[]} [groupByColumns] - The columns used to determine task grouping.
 *
 * @returns {TaskGroup[]} - A new array of task groups with the added task.
 *
 * @example
 * const updatedTaskGroups = addAndRepositionTask(taskGroups, newTask, [GroupByColumn.AssigneeID, GroupByColumn.Status]);
 */
export function addAndRepositionTask(
    taskGroups: TaskGroup[],
    newTask: Task,
    groupByColumns: GroupByColumn[]
): TaskGroup[] {
    // Step 1: Find the group that matches the new task's attributes
    let newGroupIndex: number | null = null;

    taskGroups.forEach((group, groupIndex) => {
        const groupMatches = groupByColumns.every(
            (column) => group.attributes[column] === (newTask as any)[column]
        );
        if (groupMatches) {
            newGroupIndex = groupIndex;
        }
    });

    // Step 2: Create a new immutable array
    let newTaskGroups = [...taskGroups];

    // Step 3: If a matching group exists, add the task to it
    if (newGroupIndex !== null) {
        newTaskGroups[newGroupIndex] = {
            ...newTaskGroups[newGroupIndex],
            tasks: [...newTaskGroups[newGroupIndex].tasks, newTask],
        };
    } else {
        // Step 4: Create a new group with attributes based on the task properties
        const newGroupAttributes: TaskGroupAttributes = {};

        groupByColumns.forEach((column) => {
            if (newTask[column] !== undefined) {
                newGroupAttributes[column] = newTask[column] as string;
            }
        });

        newTaskGroups.push({
            attributes: newGroupAttributes,
            tasks: [newTask],
        });
    }

    return newTaskGroups;
}

/**
 * Adds and repositions multiple new tasks within the task groups based on the specified grouping criteria.
 *
 * This function:
 * 1. Determines the appropriate groups for the new tasks based on `groupByColumns`.
 * 2. If a matching group exists, the tasks are added to that group.
 * 3. If no matching group is found, a new group is created with the appropriate attributes.
 * 4. Returns an updated list of `TaskGroup` objects reflecting the new tasks placement.
 *
 * @param {TaskGroup[]} taskGroups - The current list of task groups.
 * @param {Task[]} newTasks - The new tasks to add and reposition.
 * @param {GroupByColumn[]} [groupByColumns] - The columns used to determine task grouping.
 *
 * @returns {TaskGroup[]} - A new array of task groups with the added tasks.
 *
 * @example
 * const updatedTaskGroups = addAndRepositionTasks(taskGroups, newTasks, [GroupByColumn.AssigneeID, GroupByColumn.Status]);
 */
export function addAndRepositionTasks(
    taskGroups: TaskGroup[],
    newTasks: Task[],
    groupByColumns: GroupByColumn[]
): TaskGroup[] {
    // Step 1: Create a new immutable array
    let newTaskGroups = [...taskGroups];

    // Step 2: Iterate through each new task and find/add to the appropriate group
    newTasks.forEach((newTask) => {
        let newGroupIndex: number | null = null;

        newTaskGroups.forEach((group, groupIndex) => {
            const groupMatches = groupByColumns.every(
                (column) => group.attributes[column] === (newTask as any)[column]
            );
            if (groupMatches) {
                newGroupIndex = groupIndex;
            }
        });

        // If a matching group exists, add the task to it
        if (newGroupIndex !== null) {
            newTaskGroups[newGroupIndex] = {
                ...newTaskGroups[newGroupIndex],
                tasks: [...newTaskGroups[newGroupIndex].tasks, newTask],
            };
        } else {
            // Create a new group with attributes based on the task properties
            const newGroupAttributes: TaskGroupAttributes = {};

            groupByColumns.forEach((column) => {
                if (newTask[column] !== undefined) {
                    newGroupAttributes[column] = newTask[column] as string;
                }
            });

            newTaskGroups.push({
                attributes: newGroupAttributes,
                tasks: [newTask],
            });
        }
    });

    return newTaskGroups;
}
/**
 * Removes tasks from task groups based on an array of task IDs.
 *
 * This function:
 * 1. Iterates through the task groups to find and remove the specified tasks.
 * 2. If a task group becomes empty after removal, it is also removed.
 * 3. Returns an updated list of `TaskGroup` objects without the removed tasks.
 *
 * @param {TaskGroup[]} taskGroups - The current list of task groups.
 * @param {number[]} taskIds - The IDs of the tasks to be removed.
 *
 * @returns {TaskGroup[]} - A new array of task groups with the specified tasks removed.
 *
 * @example
 * const updatedTaskGroups = removeTasks(taskGroups, [123, 456, 789]);
 * 
 * // This will remove tasks with IDs 123, 456, and 789 from taskGroups.
 */
export function removeTasks(taskGroups: TaskGroup[], taskIds: number[]): TaskGroup[] {
    return taskGroups
        .map((group) => ({
            ...group,
            tasks: group.tasks.filter((task) => !taskIds.includes(task.task_id)),
        }))
        .filter((group) => group.tasks.length > 0); // Remove empty groups
}
export function generateTaskGroupId(attributes: TaskGroupAttributes): string {
    // Create an array to hold the attribute keys and values
    const attributeStrings: string[] = [];

    // Loop through the attributes and create a string for each key-value pair
    for (const key in attributes) {
        // Make sure TypeScript knows that key is a valid key of TaskGroupAttributes
        const attributeKey = key as keyof TaskGroupAttributes;

        if (attributes[attributeKey]) {
            attributeStrings.push(`${attributeKey}:${attributes[attributeKey]}`);
        }
    }

    // Join the array of attribute strings into a single string
    return attributeStrings.join('-');
}
